/**
 * Dados simulados do modulo de funis (RNF19) -- NAO E PRODUCAO.
 *
 * Como o numero nasce:
 *  1. `seed = hash(companyId + kind)`: empresa diferente, funil diferente ->
 *     numeros diferentes, sem nenhum Math.random. O mesmo estado mostra sempre
 *     a mesma tela.
 *  2. Volume proporcional aos DIAS da janela do cabecalho (`rangeDays`). Com
 *     "Desde o inicio" a janela e a idade da empresa no catalogo (30..365 dias),
 *     para "tudo" nunca mostrar menos que "ultimos 90 dias".
 *  3. Cada oferta e uma fatia independente; a soma das fatias e o total. A
 *     regua e uma CASCATA em fluxo estacionario: quem chega ao toque N e
 *     exatamente quem passou do toque N-1 sem pagar, responder ou sair. Isso e
 *     o que faz "entraram" bater com a soma dos destinos mais "chegaram ao fim
 *     sem voltar". "Na fila hoje" e um retrato de agora, fora dessa soma.
 *  4. Marcos que o funil ainda nao teve tempo de alcancar (empresa mais nova
 *     que D+90, por exemplo) vem `measurable: false` -- a tela diz "ainda sem
 *     como medir" em vez de inventar zero.
 *  5. A janela ANTERIOR (`previous`) sai do mesmo gerador com outro ruido:
 *     mesmo tamanho de janela, volumes na mesma ordem de grandeza, variacao
 *     de -30% a +30%. Com "Desde o inicio" nao ha janela anterior e o campo
 *     vem `null` -- a tela diz "sem periodo anterior" em vez de "0%".
 */

import { AGORA_SIMULADO, menosMinutos } from '../../lib/api';
import { empresaPorId, ofertasDaEmpresa } from '../../lib/mock/catalogo';
import { rangeDays } from '../../lib/period';
import type { CompanySummary, PeriodRange, ProductOffer } from '../../types';
import type {
  CreditChannel,
  EmailMetrics,
  FunnelDailyPoint,
  FunnelKind,
  FunnelLead,
  FunnelLeadStatus,
  FunnelOfferSlice,
  FunnelOverview,
  OfferMetrics,
  PreviousPeriodMetrics,
  TouchDefinition,
  TouchMetrics,
  VoiceMetrics,
} from './types';

// -----------------------------------------------------------------------------
// Reguas padrao (o que o backend vai devolver como configuracao da empresa)
// -----------------------------------------------------------------------------

const MIN = 1;
const HORA = 60;
const DIA = 1440;

export const TOQUES_PADRAO: Readonly<Record<FunnelKind, readonly TouchDefinition[]>> = {
  carrinho_abandonado: [
    {
      id: 'T30m',
      order: 1,
      delayMinutes: 30 * MIN,
      channel: 'voice',
      body:
        'Oi, {primeiro_nome}! Aqui é a assistente da {empresa}. Vi que você começou a comprar o {produto} e o pagamento não concluiu.\nPosso te ajudar a finalizar agora? Se travou alguma coisa — cartão, PIX ou dúvida sobre o que está incluso — a gente resolve nesta ligação. Se preferir, te mando o link pelo WhatsApp.',
      buttons: [],
      maxAttempts: 2,
      email: null,
      enabled: true,
      isLast: false,
    },
    {
      id: 'T1h',
      order: 2,
      delayMinutes: 1 * HORA,
      channel: 'whatsapp',
      body:
        'Oi, {primeiro_nome}! Vi que o seu {produto} ficou parado no checkout. Quer terminar agora?\nÉ só tocar no botão aqui embaixo — está tudo salvo do jeito que você deixou.',
      buttons: [
        { label: 'Finalizar compra', kind: 'link' },
        { label: 'Tenho uma dúvida', kind: 'reply' },
      ],
      maxAttempts: 1,
      email: {
        subject: '{primeiro_nome}, seu {produto} ficou parado no checkout',
        preheader: 'Dá pra terminar em menos de um minuto, sem preencher nada de novo.',
        body:
          'Oi, {primeiro_nome}!\nVi que você começou a finalizar o {produto} e o pagamento não chegou a ser concluído. Está tudo salvo do jeito que você deixou — dá pra terminar em menos de um minuto, sem preencher nada de novo.\nSe travou alguma coisa no caminho (cartão recusado, PIX que não gerou, dúvida sobre o que está incluso), é só voltar pelo botão abaixo: a página deixa você escolher outra forma de pagamento.\nSe preferir, copie este endereço: {link}',
        cta: 'Finalizar minha compra',
      },
      enabled: true,
      isLast: false,
    },
    {
      id: 'T24h',
      order: 3,
      delayMinutes: 24 * HORA,
      channel: 'whatsapp',
      body:
        'Oi, {primeiro_nome}! O seu {produto} continua reservado.\nSe travou no pagamento, me fala aqui que a gente resolve — ou conclua pelo botão.',
      buttons: [
        { label: 'Finalizar compra', kind: 'link' },
        { label: 'Não consegui pagar', kind: 'reply' },
      ],
      maxAttempts: 1,
      email: {
        subject: 'Ainda dá tempo de garantir o seu {produto}',
        preheader: 'Seu pedido continua reservado, esperando só o pagamento.',
        body:
          'Oi, {primeiro_nome}!\nSeu {produto} continua reservado aqui, esperando só o pagamento — ninguém perde o lugar por ter parado no meio.\nNa maioria das vezes o que segura é uma dúvida simples: forma de pagamento, o que está incluso, como funciona o acesso. Tudo isso está na própria página do checkout, no botão aqui embaixo, antes de você confirmar qualquer coisa.\nEndereço direto: {link}',
        cta: 'Retomar de onde parei',
      },
      enabled: true,
      isLast: false,
    },
    {
      id: 'T48h',
      order: 4,
      delayMinutes: 48 * HORA,
      channel: 'whatsapp',
      body:
        'Último aviso, {primeiro_nome}: vou encerrar a reserva do seu {produto}.\nSe ainda quiser, é só concluir pelo botão aqui embaixo. Se não fizer sentido agora, tudo bem — não volto a te procurar.',
      buttons: [
        { label: 'Finalizar compra', kind: 'link' },
        { label: 'Não tenho interesse', kind: 'reply' },
      ],
      maxAttempts: 1,
      email: {
        subject: 'Último aviso sobre o seu {produto}, {primeiro_nome}',
        preheader: 'Depois deste e-mail eu encerro o pedido e paro de te avisar.',
        body:
          'Oi, {primeiro_nome}!\nEste é o último aviso sobre o {produto} que ficou parado no checkout. Depois dele eu encerro o seu pedido e não volto a te procurar.\nSe ainda faz sentido pra você, é só concluir no botão aqui embaixo. Se não faz, pode ignorar este e-mail — sem ressentimento.\nEndereço direto: {link}',
        cta: 'Concluir minha compra',
      },
      enabled: true,
      isLast: true,
    },
  ],
  expirados: [
    {
      id: 'D15',
      order: 1,
      delayMinutes: 15 * DIA,
      channel: 'whatsapp',
      body:
        'Oi, {primeiro_nome}! Faz 15 dias que seu acesso ao {plano} está parado.\nSeu histórico e o seu progresso continuam exatamente como você deixou, então dá pra retomar sem recomeçar nada.\nSe fizer sentido voltar agora, é só tocar no botão aqui embaixo.',
      buttons: [
        { label: 'Reativar acesso', kind: 'link' },
        { label: 'Foi o preço', kind: 'reply' },
      ],
      maxAttempts: 1,
      email: {
        subject: '{primeiro_nome}, seu acesso ao {plano} está parado há 15 dias',
        preheader: 'Seu progresso e seu histórico continuam salvos.',
        body:
          'Oi, {primeiro_nome}!\nFaz 15 dias que o seu acesso ao {plano} venceu. Seu progresso, suas estatísticas e todo o seu histórico continuam exatamente como você deixou.\nSe voltar agora, você retoma de onde parou — não recomeça nada.\nEndereço direto: {link}',
        cta: 'Reativar meu acesso',
      },
      enabled: true,
      isLast: false,
    },
    {
      id: 'D30',
      order: 2,
      delayMinutes: 30 * DIA,
      channel: 'whatsapp',
      body:
        'Oi, {primeiro_nome}! Seu acesso ao {plano} está parado há um mês.\nTudo continua salvo aqui. Se voltar, você retoma exatamente de onde parou.',
      buttons: [
        { label: 'Voltar a usar', kind: 'link' },
        { label: 'Parei de usar', kind: 'reply' },
      ],
      maxAttempts: 1,
      email: {
        subject: 'Um mês parado, {primeiro_nome} — seu histórico continua aqui',
        preheader: 'Nada foi apagado. Voltar agora custa menos que recomeçar depois.',
        body:
          'Oi, {primeiro_nome}!\nSeu acesso ao {plano} está parado há um mês. Nada foi apagado: progresso, estatísticas e histórico seguem salvos na sua conta.\nVoltar agora custa bem menos do que recomeçar depois. Se der pra retomar, é só clicar no botão aqui embaixo.\nEndereço direto: {link}',
        cta: 'Voltar a usar',
      },
      enabled: true,
      isLast: false,
    },
    {
      id: 'D60',
      order: 3,
      delayMinutes: 60 * DIA,
      channel: 'whatsapp',
      body:
        'Oi, {primeiro_nome}, dois meses fora.\nA conta que importa: quem para dois meses e volta consegue recuperar o ritmo. Quem para e não volta recomeça do zero.\nSeu acesso ao {plano} está aqui quando quiser retomar.',
      buttons: [
        { label: 'Reativar acesso', kind: 'link' },
        { label: 'Não consigo agora', kind: 'reply' },
      ],
      maxAttempts: 1,
      email: {
        subject: 'Dois meses fora, {primeiro_nome} — ainda dá pra recuperar',
        preheader: 'Sua conta continua inteira: progresso, estatísticas e histórico.',
        body:
          'Oi, {primeiro_nome}!\nJá são dois meses desde que o seu acesso ao {plano} venceu. A conta que importa é essa: quem para dois meses e volta consegue recuperar o ritmo; quem para e não volta recomeça do zero.\nSua conta continua inteira aqui — progresso, estatísticas e histórico no lugar. Se quiser retomar, é só reativar pelo botão aqui embaixo.\nEndereço direto: {link}',
        cta: 'Reativar minha assinatura',
      },
      enabled: true,
      isLast: false,
    },
    {
      id: 'D90',
      order: 4,
      delayMinutes: 90 * DIA,
      channel: 'whatsapp',
      body:
        'Oi, {primeiro_nome}! Faz 3 meses que você não usa o {plano} e esse é meu último contato sobre isso.\nSe quiser retomar, me responde aqui que eu vejo a melhor condição pra você.\nDepois disso eu não volto a te procurar, sem ressentimento.',
      buttons: [
        { label: 'Quero voltar', kind: 'link' },
        { label: 'Não tenho interesse', kind: 'reply' },
      ],
      maxAttempts: 1,
      email: {
        subject: '{primeiro_nome}, este é meu último e-mail sobre o {plano}',
        preheader: 'Depois deste e-mail eu não volto a te procurar.',
        body:
          'Oi, {primeiro_nome}!\nFaz três meses que o seu acesso ao {plano} está parado, e este é o meu último contato sobre isso.\nSua conta e todo o seu histórico continuam salvos — nada foi perdido nesse tempo. Se quiser retomar, é só clicar no botão aqui embaixo. Se não for o momento, tudo bem: depois deste e-mail eu não volto a te procurar.\nEndereço direto: {link}',
        cta: 'Retomar minha assinatura',
      },
      enabled: true,
      isLast: true,
    },
  ],
};

// -----------------------------------------------------------------------------
// Ruido deterministico (sem Math.random)
// -----------------------------------------------------------------------------

function hashString(texto: string): number {
  let h = 2166136261;
  for (let i = 0; i < texto.length; i += 1) {
    h ^= texto.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

/** Valor em [0, 1) fixo para (seed, i). */
function ruido(seed: number, i: number): number {
  let x = (seed ^ Math.imul(i + 1, 0x9e3779b1)) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b) >>> 0;
  x ^= x >>> 16;
  return (x >>> 0) / 4294967296;
}

function clamp(valor: number, minimo: number, maximo: number): number {
  return Math.min(maximo, Math.max(minimo, valor));
}

/** Reparte `total` em inteiros proporcionais aos pesos, somando exatamente `total`. */
function distribuir(total: number, pesos: readonly number[]): number[] {
  const somaPesos = pesos.reduce((acc, p) => acc + p, 0);
  if (total <= 0 || pesos.length === 0 || somaPesos <= 0) {
    return pesos.map(() => 0);
  }
  const brutos = pesos.map((p) => (total * p) / somaPesos);
  const inteiros = brutos.map((b) => Math.floor(b));
  let falta = total - inteiros.reduce((acc, v) => acc + v, 0);
  const ordem = brutos
    .map((b, i) => ({ i, resto: b - Math.floor(b) }))
    .sort((a, b) => b.resto - a.resto);
  for (const { i } of ordem) {
    if (falta <= 0) {
      break;
    }
    inteiros[i] = (inteiros[i] ?? 0) + 1;
    falta -= 1;
  }
  return inteiros;
}

// -----------------------------------------------------------------------------
// Janela
// -----------------------------------------------------------------------------

function idadeEmDias(empresa: CompanySummary): number {
  const criada = new Date(empresa.createdAt).getTime();
  if (Number.isNaN(criada)) {
    return 30;
  }
  return Math.max(1, Math.floor((AGORA_SIMULADO.getTime() - criada) / 86_400_000));
}

function diasDaJanela(range: PeriodRange, empresa: CompanySummary): number {
  const dias = rangeDays(range);
  if (dias === null) {
    return clamp(idadeEmDias(empresa), 30, 365);
  }
  return clamp(dias, 1, 365);
}

function eixoDeDias(range: PeriodRange, dias: number): readonly string[] {
  const fim = range.to === null ? AGORA_SIMULADO : new Date(range.to);
  const inicio = new Date(fim.getTime() - dias * 86_400_000);
  return Array.from({ length: dias }, (_, i) => new Date(inicio.getTime() + i * 86_400_000).toISOString().slice(0, 10));
}

function empresaGenerica(companyId: string): CompanySummary {
  return {
    id: companyId,
    name: 'Empresa',
    slug: companyId,
    segment: '',
    logoInitials: 'EM',
    logoTone: 1,
    logoUrl: null,
    feeBps: 1_500,
    billingModel: 'a_definir',
    status: 'ativa',
    providers: ['kiwify'],
    funnels: ['carrinho_abandonado', 'expirados'],
    contactName: 'Responsável',
    contactEmail: 'contato@empresa.exemplo',
    createdAt: menosMinutos(60 * 24 * 60),
  };
}

// -----------------------------------------------------------------------------
// Cascata da regua
// -----------------------------------------------------------------------------

interface ParcialDoToque {
  readonly touch: TouchDefinition;
  readonly measurable: boolean;
  readonly queued: number;
  readonly reached: number;
  readonly delivered: number;
  readonly opened: number;
  readonly clicked: number;
  readonly paid: number;
  readonly paidByButton: number;
  readonly paidByEmail: number;
  readonly replied: number;
  readonly closedBySeller: number;
  readonly unsubscribed: number;
  readonly failed: number;
  readonly saida: number;
  readonly voice: VoiceMetrics | null;
  readonly email: EmailMetrics | null;
}

function cascata(
  kind: FunnelKind,
  toques: readonly TouchDefinition[],
  base: number,
  dias: number,
  idadeDias: number,
  r: (i: number) => number,
): readonly ParcialDoToque[] {
  const v = (valor: number, i: number, amplitude = 0.2): number => valor * (1 - amplitude + 2 * amplitude * r(i));
  const fatorFunil = kind === 'expirados' ? 0.55 : 1;
  const parciais: ParcialDoToque[] = [];
  let restante = base;

  toques.forEach((toque, i) => {
    const anterior = toques[i - 1];
    const gapAntesDias = (toque.delayMinutes - (anterior?.delayMinutes ?? 0)) / DIA;
    const mensuravel = toque.delayMinutes / DIA <= idadeDias;
    const semente = 10 + i * 20;

    // Fluxo estacionario: quem chega a este toque na janela e quem passou do
    // anterior sem sair -- inclusive gente que entrou ANTES da janela (D+60 numa
    // janela de 30 dias dispara para quem venceu ha 60). "Na fila hoje" e um
    // retrato de agora (fluxo diario x intervalo ate o marco) e NAO e
    // descontado da cascata: e outra pergunta, respondida no mesmo cartao.
    const queued = Math.ceil((restante * gapAntesDias) / dias);

    if (!mensuravel || !toque.enabled) {
      parciais.push({
        touch: toque,
        measurable: mensuravel,
        queued,
        reached: 0,
        delivered: 0,
        opened: 0,
        clicked: 0,
        paid: 0,
        paidByButton: 0,
        paidByEmail: 0,
        replied: 0,
        closedBySeller: 0,
        unsubscribed: 0,
        failed: 0,
        saida: 0,
        voice: null,
        email: null,
      });
      return;
    }

    const reached = restante;

    if (toque.channel === 'voice') {
      const answered = Math.round(reached * v(0.44, semente));
      const voicemail = Math.min(reached - answered, Math.round(reached * v(0.17, semente + 1)));
      const noAnswer = Math.max(0, reached - answered - voicemail);
      const failed = Math.min(noAnswer, Math.round(reached * 0.01));
      // RF08: quem nao atendeu recebe UMA segunda tentativa, nunca mais que isso.
      const calls = reached + voicemail + noAnswer;
      const paidAfterCall = Math.round(answered * v(0.15, semente + 2));
      parciais.push({
        touch: toque,
        measurable: true,
        queued,
        reached,
        delivered: reached,
        opened: answered,
        clicked: 0,
        paid: paidAfterCall,
        paidByButton: 0,
        paidByEmail: 0,
        replied: 0,
        closedBySeller: 0,
        unsubscribed: 0,
        failed,
        saida: paidAfterCall,
        voice: { calls, answered, voicemail, noAnswer, failed, paidAfterCall },
        email: null,
      });
      restante = reached - paidAfterCall;
      return;
    }

    const delivered = Math.round(reached * v(0.93, semente, 0.04));
    const failed = Math.min(reached - delivered, Math.round(reached * 0.008));
    const opened = Math.round(delivered * v(0.62, semente + 1));
    const clicked = Math.round(opened * v(0.11, semente + 2) * fatorFunil);
    const paidByButton = Math.round(clicked * v(0.42, semente + 3));

    let email: EmailMetrics | null = null;
    let paidByEmail = 0;
    if (toque.email !== null) {
      const skipped = Math.round(reached * 0.02);
      const sent = Math.max(0, reached - skipped);
      const fallback = Math.min(sent, reached - delivered);
      const eOpened = Math.round(sent * v(0.18, semente + 4));
      const eClicked = Math.round(eOpened * v(0.08, semente + 5) * fatorFunil);
      paidByEmail = Math.round(eClicked * 0.4);
      email = { sent, fallback, skipped, opened: eOpened, clicked: eClicked, paid: paidByEmail };
    }

    const paidOther = Math.round(opened * 0.012 * fatorFunil);
    const paid = paidByButton + paidByEmail + paidOther;
    const replied = Math.min(opened - paid, Math.round(opened * v(0.16, semente + 6)));
    const closedBySeller = Math.round(replied * v(0.27, semente + 7));
    const unsubscribed = Math.min(Math.max(0, reached - paid - replied), Math.round(reached * 0.005));
    const saida = paid + replied + unsubscribed;

    parciais.push({
      touch: toque,
      measurable: true,
      queued,
      reached,
      delivered,
      opened,
      clicked,
      paid,
      paidByButton,
      paidByEmail,
      replied,
      closedBySeller,
      unsubscribed,
      failed,
      saida,
      voice: null,
      email,
    });
    restante = Math.max(0, reached - saida);
  });

  return parciais;
}

function fecharToques(parciais: readonly ParcialDoToque[], priceCents: number): readonly TouchMetrics[] {
  return parciais.map((p, i) => {
    const proximo = parciais[i + 1];
    const seguiu = Math.max(0, p.reached - p.saida);
    const ultimo = proximo === undefined;
    const waitingNext = ultimo ? 0 : proximo.queued;
    return {
      touchId: p.touch.id,
      measurable: p.measurable,
      queued: p.queued,
      reached: p.reached,
      delivered: p.delivered,
      opened: p.opened,
      clicked: p.clicked,
      paid: p.paid,
      paidByButton: p.paidByButton,
      paidAmountCents: p.paid * priceCents,
      replied: p.replied,
      closedBySeller: p.closedBySeller,
      closedBySellerAmountCents: p.closedBySeller * priceCents,
      unsubscribed: p.unsubscribed,
      waitingNext,
      movedOn: ultimo ? 0 : seguiu,
      endedWithoutReturn: ultimo ? seguiu : 0,
      failed: p.failed,
      stuckAmountCents: p.queued * priceCents,
      voice: p.voice,
      email: p.email,
    };
  });
}

// -----------------------------------------------------------------------------
// Fatia por oferta
// -----------------------------------------------------------------------------

function simularOferta(
  kind: FunnelKind,
  oferta: ProductOffer,
  indice: number,
  seed: number,
  dias: number,
  idadeDias: number,
  feeBps: number,
  toques: readonly TouchDefinition[],
  eixo: readonly string[],
  temAnterior: boolean,
): OfferMetrics {
  const r = (i: number): number => ruido(seed + indice * 101, i);
  const v = (valor: number, i: number, amplitude = 0.2): number => valor * (1 - amplitude + 2 * amplitude * r(i));

  const porDia = kind === 'expirados' ? 0.6 + 2.2 * r(1) : 1.5 + 5 * r(1);
  const fatorPreco = clamp(1.6 - oferta.priceCents / 150_000, 0.5, 1.6);
  const checkouts = Math.max(0, Math.round(dias * porDia * fatorPreco));
  const paidOnTime = kind === 'carrinho_abandonado' ? Math.round(checkouts * v(0.28, 2)) : 0;
  const entered = checkouts - paidOnTime;
  const dueToday = kind === 'expirados' ? Math.min(entered, Math.ceil(entered / dias)) : 0;
  const returnedAlone = kind === 'expirados' ? Math.min(entered, Math.round(entered * v(0.08, 3))) : 0;

  const parciais = cascata(kind, toques, entered - returnedAlone, dias, idadeDias, r);
  const touches = fecharToques(parciais, oferta.priceCents);

  const porCanal: Record<CreditChannel, number> = { voice: 0, whatsapp: 0, email: 0, seller: 0 };
  let withSeller = 0;
  let unsubscribed = 0;
  for (const p of parciais) {
    if (p.voice !== null) {
      porCanal.voice += p.voice.paidAfterCall;
    } else {
      porCanal.email += p.paidByEmail;
      porCanal.whatsapp += p.paid - p.paidByEmail;
    }
    porCanal.seller += p.closedBySeller;
    withSeller += p.replied - p.closedBySeller;
    unsubscribed += p.unsubscribed;
  }

  const recovered = porCanal.voice + porCanal.whatsapp + porCanal.email + porCanal.seller;
  // Quem entrou e nao teve nenhum desfecho (pagou, respondeu, saiu): e o
  // terceiro degrau do caminho. Sai por diferenca para os tres degraus somarem.
  const stillOut = Math.max(0, entered - returnedAlone - recovered - withSeller - unsubscribed);
  const recoveredAmountCents = recovered * oferta.priceCents;
  const carryOverRecovered = dias >= 2 ? Math.round(recovered * Math.min(0.4, 7 / dias) * v(1, 7)) : 0;
  const carryOverAmountCents = carryOverRecovered * oferta.priceCents;
  const commissionCents = Math.floor(((recoveredAmountCents + carryOverAmountCents) * feeBps) / 10_000);

  // Serie diaria: fim de semana mais fraco, com ruido por dia; soma bate com o total.
  const pesosEntrada = eixo.map((dia, i) => {
    const semana = new Date(`${dia}T00:00:00.000Z`).getUTCDay();
    return (semana === 0 || semana === 6 ? 0.55 : 1) * (0.7 + 0.6 * r(100 + i));
  });
  const entradas = distribuir(entered, pesosEntrada);
  const recuperacoes = distribuir(
    recovered,
    pesosEntrada.map((p, i) => p * (0.6 + 0.8 * r(300 + i))),
  );
  const daily: FunnelDailyPoint[] = eixo.map((day, i) => {
    const rec = recuperacoes[i] ?? 0;
    return { day, entered: entradas[i] ?? 0, recovered: rec, recoveredAmountCents: rec * oferta.priceCents };
  });

  // Janela anterior: mesmas formulas, outro ruido (offset 500). A taxa de
  // recuperacao anterior parte da atual com variacao propria, para o delta ser
  // plausivel (nem sempre positivo, nem sempre negativo).
  let previous: PreviousPeriodMetrics | null = null;
  if (temAnterior) {
    const rp = (i: number): number => ruido(seed + indice * 101 + 500, i);
    const vp = (valor: number, i: number, amplitude = 0.3): number => valor * (1 - amplitude + 2 * amplitude * rp(i));
    const checkoutsAnt = Math.max(0, Math.round(checkouts * vp(1, 1)));
    const paidOnTimeAnt = kind === 'carrinho_abandonado' ? Math.min(checkoutsAnt, Math.round(checkoutsAnt * v(0.28, 2))) : 0;
    const enteredAnt = checkoutsAnt - paidOnTimeAnt;
    const recoveredAnt = entered === 0 ? 0 : Math.min(enteredAnt, Math.round((enteredAnt * recovered * vp(1, 3)) / entered));
    const recoveredAmountAnt = recoveredAnt * oferta.priceCents;
    previous = {
      checkouts: checkoutsAnt,
      paidOnTime: paidOnTimeAnt,
      entered: enteredAnt,
      recovered: recoveredAnt,
      recoveredAmountCents: recoveredAmountAnt,
      commissionCents: Math.floor((recoveredAmountAnt * feeBps) / 10_000),
    };
  }

  return {
    checkouts,
    paidOnTime,
    entered,
    dueToday,
    returnedAlone,
    returnedAloneAmountCents: returnedAlone * oferta.priceCents,
    recovered,
    recoveredAmountCents,
    recoveredByChannel: porCanal,
    recoveredAmountByChannelCents: {
      voice: porCanal.voice * oferta.priceCents,
      whatsapp: porCanal.whatsapp * oferta.priceCents,
      email: porCanal.email * oferta.priceCents,
      seller: porCanal.seller * oferta.priceCents,
    },
    carryOverRecovered,
    carryOverAmountCents,
    commissionCents,
    withSeller,
    unsubscribed,
    stillOut,
    stuckAmountCents: stillOut * oferta.priceCents,
    touches,
    daily,
    previous,
  };
}

// -----------------------------------------------------------------------------
// Leads do funil
// -----------------------------------------------------------------------------

const NOMES: readonly (string | null)[] = [
  'Mariana Ferraz',
  'João Batista Lemos',
  'Aline Souza Prado',
  'Ricardo Nunes',
  'Beatriz Camargo',
  'Carlos Eduardo Pires',
  'Fernanda Loureiro',
  null,
  'Gustavo Henrique Sá',
  'Helena Martins',
  'Igor Vasconcelos',
  'Juliana Peixoto',
  'Kauã Ribeiro',
  'Larissa Fontes',
  'Marcelo Antunes',
  'Natália Brito',
  null,
  'Otávio Mendes',
  'Patrícia Guimarães',
  'Rafael Toledo',
  'Sabrina Lacerda',
  'Tiago Moreira',
  'Vanessa Coutinho',
  'Wesley Dantas',
];

const DDDS: readonly string[] = ['11', '21', '31', '41', '51', '61', '71', '81', '85', '92'];

function statusDoLead(kind: FunnelKind, sorteio: number): FunnelLeadStatus {
  if (kind === 'expirados' && sorteio < 0.05) {
    return 'voltou_sozinho';
  }
  if (sorteio < 0.17) {
    return 'pagou';
  }
  if (sorteio < 0.27) {
    return 'com_comercial';
  }
  if (sorteio < 0.35) {
    return 'vendedora_fechou';
  }
  if (sorteio < 0.38) {
    return 'descadastrou';
  }
  if (sorteio < 0.56) {
    return 'sem_voltar';
  }
  return 'na_regua';
}

function simularLeads(
  kind: FunnelKind,
  companyId: string,
  slices: readonly FunnelOfferSlice[],
  toques: readonly TouchDefinition[],
  seed: number,
  dias: number,
): readonly FunnelLead[] {
  const totalEntradas = slices.reduce((acc, s) => acc + s.metrics.entered, 0);
  const quantidade = Math.min(36, totalEntradas, Math.max(0, slices.length * 9));
  if (quantidade === 0) {
    return [];
  }
  const minutosDaJanela = dias * DIA;
  const passo = Math.max(1, Math.floor(minutosDaJanela / (quantidade + 1)));
  const ultimoToque = toques[toques.length - 1];

  return Array.from({ length: quantidade }, (_, n) => {
    const fatia = slices[n % slices.length];
    const oferta = fatia?.offer;
    const r = (i: number): number => ruido(seed + 7_000 + n * 13, i);
    const status = statusDoLead(kind, r(1));
    const haMinutos = Math.min(minutosDaJanela - 1, n * passo + Math.floor(r(2) * passo * 0.8) + 5);
    const enteredAt = menosMinutos(haMinutos);

    // Toque que a pessoa ja alcancou, limitado pelo tempo que ela esta no funil.
    const alcancaveis = toques.filter((t) => t.delayMinutes <= haMinutos);
    const maximo = alcancaveis.length;
    let lastTouchOrder: number;
    let recoveredVia: FunnelLead['recoveredVia'] = null;
    let recoveredAt: string | null = null;
    switch (status) {
      case 'sem_voltar':
        lastTouchOrder = ultimoToque?.order ?? maximo;
        break;
      case 'voltou_sozinho':
        lastTouchOrder = 0;
        recoveredVia = null;
        recoveredAt = menosMinutos(Math.max(1, haMinutos - Math.floor(r(3) * Math.min(haMinutos, 10 * DIA))));
        break;
      case 'pagou': {
        lastTouchOrder = Math.max(1, Math.min(maximo, 1 + Math.floor(r(4) * Math.max(1, maximo))));
        const toque = toques[lastTouchOrder - 1];
        recoveredVia = toque?.channel === 'voice' ? 'voice' : r(5) < 0.2 ? 'email' : 'whatsapp';
        recoveredAt = menosMinutos(Math.max(1, haMinutos - (toque?.delayMinutes ?? 0) - Math.floor(r(6) * 90)));
        break;
      }
      case 'vendedora_fechou': {
        lastTouchOrder = Math.max(1, Math.min(maximo, 1 + Math.floor(r(4) * Math.max(1, maximo))));
        const toque = toques[lastTouchOrder - 1];
        recoveredVia = 'seller';
        recoveredAt = menosMinutos(Math.max(1, haMinutos - (toque?.delayMinutes ?? 0) - Math.floor(r(6) * 3 * DIA)));
        break;
      }
      default:
        lastTouchOrder = Math.min(maximo, Math.floor(r(4) * (maximo + 1)));
    }
    if (maximo === 0 && status !== 'voltou_sozinho') {
      lastTouchOrder = 0;
    }

    const ddd = DDDS[n % DDDS.length] ?? '11';
    return {
      id: `SIMULADO-funil-${kind === 'expirados' ? 'exp' : 'car'}-${String(n + 1).padStart(3, '0')}`,
      companyId,
      name: NOMES[n % NOMES.length] ?? null,
      phoneE164: `+55${ddd}9${String(88_100_000 + n * 7_919).padStart(8, '0')}`,
      offerId: oferta?.id ?? 'SIMULADO-oferta-000',
      offerName: oferta?.name ?? 'Produto de exemplo',
      amountCents: oferta?.priceCents ?? 9_900,
      provider: oferta?.provider ?? 'kiwify',
      enteredAt,
      lastTouchOrder,
      status: maximo === 0 && status === 'sem_voltar' ? 'na_regua' : status,
      recoveredAt,
      recoveredVia,
    };
  });
}

// -----------------------------------------------------------------------------
// Entrada
// -----------------------------------------------------------------------------

export function simularFunil(kind: FunnelKind, companyId: string, range: PeriodRange): FunnelOverview {
  const empresa = empresaPorId(companyId) ?? empresaGenerica(companyId);
  const ofertas = ofertasDaEmpresa(companyId).filter((o) => o.active && (kind === 'expirados' ? o.recurring : true));
  const dias = diasDaJanela(range, empresa);
  const idadeDias = idadeEmDias(empresa);
  const seed = hashString(`${companyId}:${kind}`);
  const toques = TOQUES_PADRAO[kind];
  const eixo = eixoDeDias(range, dias);

  const slices: FunnelOfferSlice[] = ofertas.map((o, k) => ({
    offer: { id: o.id, name: o.name, priceCents: o.priceCents, recurring: o.recurring, provider: o.provider },
    metrics: simularOferta(kind, o, k, seed, dias, idadeDias, empresa.feeBps, toques, eixo, range.from !== null),
  }));

  return {
    kind,
    companyId,
    companyName: empresa.name,
    feeBps: empresa.feeBps,
    responsible: empresa.contactName,
    active: empresa.status !== 'pausada',
    funnelSinceAt: empresa.createdAt,
    periodStart: range.from ?? empresa.createdAt,
    periodEnd: range.to ?? AGORA_SIMULADO.toISOString(),
    touches: toques,
    slices,
    leads: simularLeads(kind, companyId, slices, toques, seed, dias),
  };
}
